import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Loader2, Mail, Send, RotateCcw, Eye, ExternalLink, AlertTriangle,
  CheckCircle2, PauseCircle, Radio, FlaskConical, Search, ArrowLeft, Inbox,
  Copy, Presentation,
} from "lucide-react";

/*
 * Tracker Admin — the control room behind the Client Tracker.
 *
 * The tracker now emails clients about money on a schedule. This is the page
 * that has to be able to answer, without anyone reading code: what emails
 * exist, what they say, what has actually gone out and to whom, and what every
 * project looks like from BOTH sides.
 *
 * The send mode is the loudest thing on the page, deliberately and everywhere.
 * The single most expensive mistake available here is believing the module is
 * paused while it is live, so the answer is never more than a glance away.
 */

const TABS = [
  { key: "overview", label: "Projects", hint: "Every tracker, both sides" },
  { key: "map", label: "Lifecycle map", hint: "Every email HQ sends, in order" },
  { key: "flows", label: "Email flows", hint: "What sends, and when" },
  { key: "log", label: "Sent log", hint: "What actually went out" },
  { key: "settings", label: "Settings", hint: "Arm each flow of emails" },
];

const MODES = {
  paused: {
    label: "Paused", icon: PauseCircle,
    cls: "bg-muted text-muted-foreground border-border",
    blurb: "Emails are rendered and logged, but nothing is sent. Safe to test against.",
  },
  test: {
    label: "Test mode", icon: FlaskConical,
    cls: "bg-amber-100 text-amber-900 border-amber-200",
    blurb: "Every email is redirected to your test address. Clients receive nothing.",
  },
  live: {
    label: "Live", icon: Radio,
    cls: "bg-red-100 text-red-900 border-red-200",
    blurb: "Emails are going to real clients.",
  },
};

const STATUS_CLS = {
  sent: "bg-green-100 text-green-800 border-green-200",
  redirected: "bg-amber-100 text-amber-900 border-amber-200",
  paused: "bg-muted text-muted-foreground border-border",
  failed: "bg-red-100 text-red-800 border-red-200",
  no_recipient: "bg-red-50 text-red-700 border-red-200",
  unknown_template: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_LABEL = {
  sent: "Sent", redirected: "Redirected to test", paused: "Suppressed",
  failed: "Failed", no_recipient: "No recipient", unknown_template: "Unknown template",
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

function Pill({ cls, children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] leading-none px-2 py-1 rounded-full border ${cls}`}>
      {children}
    </span>
  );
}

/* The rendered email itself. Sandboxed with no permissions at all: this HTML
   comes out of the database and is editable from this very page, so it is
   treated as untrusted content even though we wrote it. */
function EmailFrame({ html, height = 620 }) {
  return (
    <iframe
      title="Email preview"
      srcDoc={html || "<p style='font-family:sans-serif;color:#888;padding:20px'>Nothing to preview</p>"}
      sandbox=""
      className="w-full rounded-lg border border-border bg-white"
      style={{ height }}
    />
  );
}

/* Loudest first. The banner has to be coloured by the noisiest thing switched
   on anywhere, not by the module default — the expensive mistake is believing
   nothing is sending while one flow is live. */
const MODE_LOUDNESS = { paused: 0, test: 1, live: 2 };

function loudestMode(settings) {
  const flows = settings?.flows || [];
  if (!flows.length) return settings?.mode || "paused";
  return flows.reduce(
    (worst, f) => (MODE_LOUDNESS[f.mode] > MODE_LOUDNESS[worst] ? f.mode : worst),
    "paused",
  );
}

/* A small pill saying what one flow is doing right now. */
function ModePill({ mode }) {
  const m = MODES[mode] || MODES.paused;
  return <Pill cls={m.cls}>{m.label}</Pill>;
}

function ModeBanner({ settings, onGo }) {
  if (!settings) return null;
  const flows = settings.flows || [];
  const loudest = loudestMode(settings);
  const mixed = flows.length > 0 && flows.some((f) => f.mode !== flows[0].mode);
  const mode = MODES[loudest] || MODES.paused;
  const Icon = mode.icon;
  const showTestAddress = flows.some((f) => f.mode === "test") && settings.test_recipient;

  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${mode.cls}`}>
      <Icon className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          {mixed
            ? `Client emails are ${mode.label.toLowerCase()} for some flows`
            : `Client emails are ${mode.label.toLowerCase()}`}
          {showTestAddress ? ` → ${settings.test_recipient}` : ""}
        </p>
        {/* Named flow by flow, because "some flows" is not an answer anyone can
            act on. This is the line that has to be right at a glance. */}
        {mixed ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-1.5">
            {flows.map((f) => (
              <span key={f.key} className="inline-flex items-center gap-1.5 text-sm">
                {f.name}
                <ModePill mode={f.mode} />
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm mt-0.5">{mode.blurb}</p>
        )}
        {!settings.resend_configured && (
          <p className="text-sm mt-1.5">
            RESEND_API_KEY isn't set on this host, so nothing can send from here regardless of mode.
          </p>
        )}
      </div>
      {onGo && (
        <Button variant="outline" size="sm" className="shrink-0 bg-background" onClick={onGo}>
          Change
        </Button>
      )}
    </div>
  );
}

/* ── Projects: every tracker, from both sides ──────────────────────────────── */

/* ── The sales demo ─────────────────────────────────────────────────────────
 *
 * "Miami Portal Demo" is a mock project reps open on sales calls to show a
 * prospect the client portal. This runs the same seed as
 * `python -m scripts.seed_portal_demo`, so prod gets one without a shell.
 * Pressing it again resets the demo to its mid-project state and keeps the
 * same client link. The demo never emails anyone and never counts as money —
 * see backend/services/portal_demo.py.
 */
function DemoProjectCard() {
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState(null);

  const seed = async () => {
    setBusy(true);
    try {
      const res = await apiClient.post("/project-tracker/demo/seed");
      setDemo(res.data);
      toast.success(res.data.created ? "Demo project created" : "Demo project reset");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't build the demo project.");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied");
  };

  return (
    <Card data-testid="portal-demo-card">
      <CardContent className="pt-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium flex items-center gap-2">
              <Presentation className="w-4 h-4" /> Sales demo — “Miami Portal Demo”
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
              A mock project, mid-production, for showing prospects the client portal on a call.
              It never emails anyone and stays out of every money total. Pressing again resets
              it and keeps the same client link.
            </p>
          </div>
          <Button size="sm" className="rounded-full shrink-0" onClick={seed} disabled={busy}
            data-testid="portal-demo-seed">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
            Create / reset demo
          </Button>
        </div>
        {demo && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-24 shrink-0">Client link</span>
              <Input readOnly value={demo.public_url} className="font-mono text-xs h-8" />
              <Button variant="outline" size="sm" className="shrink-0 h-8" onClick={() => copy(demo.public_url)}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
              <a href={demo.public_url} target="_blank" rel="noreferrer" className="shrink-0">
                <Button variant="outline" size="sm" className="h-8"><ExternalLink className="w-3.5 h-3.5" /></Button>
              </a>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-24 shrink-0">In HQ</span>
              <Link to={`/programs/${demo.program_id}/tracker`} className="text-xs underline">
                Open the demo's tracker
              </Link>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OverviewTab({ data, loading }) {
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data?.rows || [];
    return (data?.rows || []).filter((r) => (r.program_name || "").toLowerCase().includes(needle));
  }, [data, q]);

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  const t = data?.totals || {};

  return (
    <div className="space-y-4">
      <DemoProjectCard />

      {/* The one thing on this page that must not be scrolled past: projects
          whose milestone schedule claims more than the contract says is owed.
          Real example in this data — an $18,000 contract, paid in full,
          carrying $359,817 of "overdue" milestones from the agreement parser. */}
      {t.blocked > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-900">
              {t.blocked} project{t.blocked === 1 ? "" : "s"} will send nothing until the schedule is fixed
            </p>
            <p className="text-sm text-red-800 mt-0.5">
              Their milestones add up to more than the contract says is outstanding — usually
              milestones the agreement parser invented. Chasing those clients would demand money
              they don't owe, so escalation refuses to run on them. They're listed first below.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Trackers", t.trackers, ""],
          ["With overdue", t.with_overdue, t.with_overdue ? "text-red-700" : ""],
          ["Past pause threshold", t.hold_recommended, t.hold_recommended ? "text-red-700" : ""],
          ["Blocked — bad schedule", t.blocked, t.blocked ? "text-red-700" : ""],
          ["Missing forecasts", t.needing_forecasts, t.needing_forecasts ? "text-amber-700" : ""],
        ].map(([label, value, cls]) => (
          <div key={label} className="rounded-xl border p-3">
            <p className={`text-2xl font-light ${cls}`}>{value ?? 0}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Find a project…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="rounded-xl border divide-y">
        {rows.length === 0 && <p className="p-6 text-sm text-muted-foreground">No projects match.</p>}
        {rows.map((r) => (
          <div key={r.program_id} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/programs/${r.program_id}/tracker`} className="text-sm font-medium hover:underline">
                    {r.program_name}
                  </Link>
                  <span className="text-xs text-muted-foreground">{r.stage}</span>
                  {r.client.paused && <Pill cls={STATUS_CLS.failed}>Paused for non-payment</Pill>}
                  {!r.integrity.ok && <Pill cls={STATUS_CLS.failed}>Emails blocked</Pill>}
                  {!r.clock_started && <Pill cls="bg-muted text-muted-foreground border-border">Clock not started</Pill>}
                </div>
                {!r.integrity.ok && (
                  <p className="text-sm text-red-700 mt-1.5">{r.integrity.reason}</p>
                )}
                {/* What the client is reading, verbatim. */}
                {r.client.banner_title && (
                  <p className={`text-sm mt-1.5 ${
                    r.client.banner_severity === "critical" || r.client.banner_severity === "serious"
                      ? "text-red-700" : "text-muted-foreground"}`}>
                    Client sees: “{r.client.banner_title}”
                  </p>
                )}
              </div>
              <a href={r.tracker_url} target="_blank" rel="noreferrer"
                className="text-xs text-muted-foreground hover:underline shrink-0 inline-flex items-center gap-1">
                Client view <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mt-3 text-xs">
              <div className="rounded-lg bg-muted/40 p-2.5">
                <p className="text-muted-foreground mb-1">Money</p>
                {r.client.has_schedule ? (
                  <>
                    <p>{r.client.outstanding_text || "—"} outstanding</p>
                    {r.client.overdue_count > 0 && (
                      <p className="text-red-700 mt-0.5">
                        {r.client.overdue_text} overdue across {r.client.overdue_count}
                        {r.client.late_fees_text ? ` · ${r.client.late_fees_text} interest` : ""}
                      </p>
                    )}
                  </>
                ) : <p className="text-muted-foreground">No schedule</p>}
              </div>
              <div className="rounded-lg bg-muted/40 p-2.5">
                <p className="text-muted-foreground mb-1">PM to-do</p>
                {r.pm.milestones_without_forecast > 0 ? (
                  <p className="text-amber-700">
                    {r.pm.milestones_without_forecast} milestone{r.pm.milestones_without_forecast === 1 ? "" : "s"} with no date
                    {r.pm.unforecast_titles.length ? ` (${r.pm.unforecast_titles.join(", ")})` : ""}
                  </p>
                ) : <p>All milestones forecast</p>}
                {r.pm.hold_recommended && (
                  <p className="text-red-700 mt-0.5">{r.pm.days_overdue} days overdue — past pause threshold</p>
                )}
              </div>
              <div className="rounded-lg bg-muted/40 p-2.5">
                <p className="text-muted-foreground mb-1">Automation</p>
                <p>
                  {r.automation.reminders_sent} reminder{r.automation.reminders_sent === 1 ? "" : "s"} sent
                  {r.automation.last_reminder_at ? ` · last ${fmtWhen(r.automation.last_reminder_at)}` : ""}
                </p>
                <p className="text-muted-foreground mt-0.5">
                  {r.automation.reminders_enabled ? "Reminders on" : "Reminders off"}
                  {" · "}
                  {r.automation.auto_hold_enabled ? "auto-pause armed" : "auto-pause off"}
                  {!r.client_email ? " · no client email" : ""}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Flows: what sends, and when ───────────────────────────────────────────── */

function FlowsTab({ flows, loading, onOpen }) {
  if (loading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  return (
    <div className="space-y-5">
      {(flows?.flows || []).map((flow) => (
        <Card key={flow.key}>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <CardTitle className="text-base">{flow.name}</CardTitle>
              {/* What this flow would do if it fired right now — each one is
                  switched separately, so a single banner can't say it. */}
              <span className="shrink-0 flex items-center gap-1.5">
                <ModePill mode={flow.mode} />
                {!flow.follows_module && (
                  <span className="text-[11px] text-muted-foreground">set on its own</span>
                )}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{flow.blurb}</p>
          </CardHeader>
          <CardContent>
            <div className="rounded-xl border divide-y">
              {flow.templates.map((t) => (
                <button key={t.key} onClick={() => onOpen(t.key)}
                  className="w-full text-left p-3 flex items-center gap-3 hover:bg-muted/40 transition-colors">
                  {/* Where this sits on the clock, for the escalation flow. */}
                  {typeof t.day_offset === "number" && (
                    <span className="shrink-0 w-14 text-xs tabular-nums text-muted-foreground">
                      {t.day_offset === 0 ? "due" : t.day_offset < 0 ? `${t.day_offset}d` : `+${t.day_offset}d`}
                    </span>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="text-sm block">{t.name}</span>
                    <span className="text-xs text-muted-foreground block truncate">{t.trigger}</span>
                  </span>
                  {t.is_customised && <Pill cls="bg-blue-100 text-blue-800 border-blue-200">Edited</Pill>}
                  <Eye className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ── Template editor ───────────────────────────────────────────────────────── */

function TemplateEditor({ templateKey, onClose, onSaved, projects }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [previewProgram, setPreviewProgram] = useState("");

  const load = useCallback(async () => {
    const res = await apiClient.get(`/tracker-admin/templates/${templateKey}`);
    setData(res.data);
    const t = res.data.template;
    setForm({
      subject: t.subject || "", heading: t.heading || "", body: t.body || "",
      cta_label: t.cta_label || "", footnote: t.footnote || "", accent: t.accent || "ink",
    });
    setPreview(res.data.preview);
  }, [templateKey]);

  useEffect(() => { load(); }, [load]);

  // Re-render from the server as you type, so what you approve is what sends —
  // a preview built by different code from the sender is not a preview.
  useEffect(() => {
    if (!data) return undefined;
    const id = setTimeout(async () => {
      try {
        const res = await apiClient.post(`/tracker-admin/templates/${templateKey}/preview`, {
          ...form, program_id: previewProgram || null,
        });
        setPreview(res.data.preview);
      } catch { /* keep the last good preview */ }
    }, 400);
    return () => clearTimeout(id);
  }, [form, previewProgram, templateKey, data]);

  if (!data) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  const t = data.template;

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put(`/tracker-admin/templates/${templateKey}`, form);
      toast.success("Saved — this is what clients will read");
      await load(); onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };

  const revert = async () => {
    if (!window.confirm("Discard your edits and go back to the default wording?")) return;
    setBusy(true);
    try {
      await apiClient.delete(`/tracker-admin/templates/${templateKey}`);
      toast.success("Reverted to the default");
      await load(); onSaved?.();
    } catch (e) {
      toast.error("Couldn't revert");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const res = await apiClient.post(`/tracker-admin/templates/${templateKey}/test-send`, {
        ...form, to: testTo, program_id: previewProgram || null,
      });
      toast.success(`Sent to ${res.data.to}`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the test");
    } finally { setBusy(false); }
  };

  const field = (k, label, hint, rows) => (
    <div>
      <Label className="text-xs">{label}</Label>
      {rows ? (
        <textarea value={form[k]} rows={rows}
          onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
      ) : (
        <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" />
      )}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onClose} className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1" /> All flows
        </Button>
        <div className="min-w-0">
          <p className="text-base font-light">{t.name}</p>
          <p className="text-xs text-muted-foreground truncate">{t.trigger}</p>
        </div>
        {t.is_customised && (
          <Pill cls="bg-blue-100 text-blue-800 border-blue-200 ml-auto shrink-0">
            Edited{t.updated_by_name ? ` by ${t.updated_by_name}` : ""}
          </Pill>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <Card>
          <CardHeader><CardTitle className="text-base">The words</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {field("subject", "Subject line", "{heading} inserts the headline below.")}
            {field("heading", "Headline", "The large line in the dark bar.")}
            {field("body", "Body", "Leave a blank line between paragraphs.", 8)}
            {field("cta_label", "Button label", "The button always links to the client's tracker.")}
            {field("footnote", "Footnote", "Small print under the divider.", 2)}

            <div>
              <Label className="text-xs">Placeholders you can use</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {(t.variables || []).map((v) => (
                  <button key={v} type="button"
                    onClick={() => { navigator.clipboard.writeText(`{${v}}`); toast.success(`{${v}} copied`); }}
                    className="text-[11px] px-2 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground">
                    {`{${v}}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button size="sm" onClick={save} disabled={busy}>
                {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Save
              </Button>
              {t.is_customised && (
                <Button size="sm" variant="ghost" onClick={revert} disabled={busy}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Revert to default
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Exactly what sends</CardTitle>
              <p className="text-sm text-muted-foreground">
                Rendered by the same code that sends it, so this is not an approximation.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Preview against</Label>
                <select value={previewProgram} onChange={(e) => setPreviewProgram(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-sm">
                  <option value="">Sample data</option>
                  {(projects || []).map((p) => (
                    <option key={p.program_id} value={p.program_id}>
                      {p.program_name}{p.client.overdue_count ? ` — ${p.client.overdue_text} overdue` : ""}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Pick a real project to see the copy with real balances and dates in it.
                </p>
              </div>
              <div className="rounded-lg border p-2.5 bg-muted/40">
                <p className="text-xs text-muted-foreground">Subject</p>
                <p className="text-sm">{preview?.subject}</p>
              </div>
              <EmailFrame html={preview?.html} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Send yourself a copy</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Goes only to the address below, and works even while the module is paused —
                which is exactly when you want to be reading these.
              </p>
              <div className="flex gap-2">
                <Input type="email" placeholder="you@therenderpros.com" value={testTo}
                  onChange={(e) => setTestTo(e.target.value)} />
                <Button size="sm" className="shrink-0" onClick={sendTest} disabled={busy || !testTo}>
                  <Send className="w-3.5 h-3.5 mr-1.5" /> Send
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ── Sent log ──────────────────────────────────────────────────────────────── */

function LogTab({ onOpen }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/tracker-admin/emails", {
        params: { limit: 100, ...(status ? { status } : {}) },
      });
      setPage(res.data);
    } catch (e) {
      toast.error("Couldn't load the log");
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const stats = page?.stats || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Total", stats.total], ["Delivered", stats.delivered],
          ["Suppressed", stats.suppressed], ["Failed", stats.failed]].map(([l, v]) => (
          <div key={l} className="rounded-xl border p-3">
            <p className={`text-2xl font-light ${l === "Failed" && v ? "text-red-700" : ""}`}>{v ?? 0}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{l}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap">
        {["", "sent", "redirected", "paused", "failed"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              status === s ? "bg-foreground text-background border-foreground" : "text-muted-foreground border-border"}`}>
            {s ? STATUS_LABEL[s] : "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : (page?.rows || []).length === 0 ? (
        <div className="rounded-xl border p-10 text-center">
          <Inbox className="w-6 h-6 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            Nothing here yet. Every send attempt lands in this log — including the ones
            that were suppressed and the ones that failed.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border divide-y">
          {page.rows.map((r) => (
            <button key={r.id} onClick={() => onOpen(r.id)}
              className="w-full text-left p-3 flex items-center gap-3 hover:bg-muted/40 transition-colors">
              <span className="flex-1 min-w-0">
                <span className="text-sm block truncate">{r.subject || r.template_name}</span>
                <span className="text-xs text-muted-foreground block truncate">
                  {r.program_name || "—"} · to {r.intended_to || "—"}
                  {r.actual_to && r.actual_to !== r.intended_to ? ` (redirected to ${r.actual_to})` : ""}
                  {r.reason ? ` · ${r.reason}` : ""}
                </span>
              </span>
              <span className="text-xs text-muted-foreground shrink-0 hidden sm:block">{fmtWhen(r.created_at)}</span>
              <Pill cls={STATUS_CLS[r.status] || STATUS_CLS.paused}>{STATUS_LABEL[r.status] || r.status}</Pill>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LogDetail({ logId, onClose }) {
  const [entry, setEntry] = useState(null);
  useEffect(() => {
    apiClient.get(`/tracker-admin/emails/${logId}`)
      .then((r) => setEntry(r.data))
      .catch(() => toast.error("Couldn't load that email"));
  }, [logId]);

  if (!entry) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onClose} className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to the log
        </Button>
        <Pill cls={STATUS_CLS[entry.status] || STATUS_CLS.paused}>{STATUS_LABEL[entry.status] || entry.status}</Pill>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {[
              ["Subject", entry.subject],
              ["Template", entry.template_name],
              ["Project", entry.program_name],
              ["Addressed to", entry.intended_to],
              ["CC", (entry.intended_cc || []).join(", ")],
              ["Actually sent to", entry.actual_to || "— not sent"],
              ["Mode at the time", entry.mode],
              ["Why", entry.reason],
              ["When", fmtWhen(entry.created_at)],
            ].map(([l, v]) => (
              <div key={l}>
                <p className="text-xs text-muted-foreground">{l}</p>
                <p className="break-words">{v || "—"}</p>
              </div>
            ))}
          </div>
          {entry.error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-900">{entry.error}</p>
            </div>
          )}
          {/* A snapshot of what was sent, not a re-render — re-rendering would
              show today's wording against today's balance. */}
          <EmailFrame html={entry.html} height={700} />
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Settings ──────────────────────────────────────────────────────────────── */

/* The per-flow choice. "Module default" is a real option rather than an absent
   value, so a flow can be handed back to the module without anyone wondering
   which of the three it silently became. */
const FLOW_CHOICES = [
  { key: "default", label: "Module default" },
  { key: "paused", label: "Paused" },
  { key: "test", label: "Test" },
  { key: "live", label: "Live" },
];

function SettingsTab({ settings, onSaved }) {
  const [mode, setMode] = useState(settings?.mode || "paused");
  const [recipient, setRecipient] = useState(settings?.test_recipient || "");
  const [flowModes, setFlowModes] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setMode(settings?.mode || "paused");
    setRecipient(settings?.test_recipient || "");
    setFlowModes(settings?.flow_modes || {});
  }, [settings]);

  const flows = settings?.flows || [];
  // What each flow WOULD do if this page were saved as it stands — resolved the
  // same way the backend resolves it, so the preview can't disagree with it.
  const resolved = (key) => flowModes[key] || mode;
  const goingLive = flows.filter((f) => resolved(f.key) === "live");
  const needsTestAddress = mode === "test" || Object.values(flowModes).includes("test");

  const save = async () => {
    if (goingLive.length && !window.confirm(
      `Going live means real clients start receiving these emails: ` +
      `${goingLive.map((f) => f.name).join(", ")}. Are you sure?`,
    )) return;
    setBusy(true);
    try {
      // Every flow is sent every time, "default" included, so clearing an
      // override is as ordinary a save as setting one.
      const payload = {
        mode,
        test_recipient: recipient,
        flow_modes: Object.fromEntries(flows.map((f) => [f.key, flowModes[f.key] || "default"])),
      };
      const res = await apiClient.put("/tracker-admin/settings", payload);
      const live = (res.data.flows || []).filter((f) => f.mode === "live");
      toast.success(
        live.length
          ? `Live for ${live.map((f) => f.name).join(", ")}`
          : "Saved — nothing is going to clients",
      );
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Send mode</CardTitle>
          <p className="text-sm text-muted-foreground">
            The default for every flow below. A flow set on its own ignores this.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(MODES).map(([key, m]) => {
            const Icon = m.icon;
            return (
              <button key={key} onClick={() => setMode(key)}
                className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition-colors ${
                  mode === key ? "border-foreground" : "border-border hover:bg-muted/40"}`}>
                <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="min-w-0">
                  <span className="text-sm font-medium block">{m.label}</span>
                  <span className="text-sm text-muted-foreground">{m.blurb}</span>
                </span>
              </button>
            );
          })}

          {needsTestAddress && (
            <div>
              <Label className="text-xs">Redirect every test email to</Label>
              <Input type="email" className="mt-1" value={recipient}
                placeholder="you@therenderpros.com"
                onChange={(e) => setRecipient(e.target.value)} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Per flow ─────────────────────────────────────────────────────────
          The reason this exists: on launch day the welcome email should reach
          clients while the collections ladder stays switched off. One module
          switch could only offer all of them or none. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Each flow on its own</CardTitle>
          <p className="text-sm text-muted-foreground">
            Turn one kind of email on without turning on the rest.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {flows.map((f) => {
            const eff = resolved(f.key);
            return (
              <div key={f.key} className="rounded-xl border p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm font-medium">{f.name}</span>
                  <ModePill mode={eff} />
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {FLOW_CHOICES.map((c) => {
                    const chosen = (flowModes[f.key] || "default") === c.key;
                    return (
                      <button key={c.key} type="button"
                        onClick={() => setFlowModes((prev) => ({ ...prev, [f.key]: c.key }))}
                        className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
                          chosen ? "border-foreground" : "border-border hover:bg-muted/40"}`}>
                        {c.key === "default" ? `Module default (${MODES[mode].label})` : c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <Button size="sm" onClick={save} disabled={busy}>
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Save
          </Button>

          {settings?.updated_by_name && (
            <p className="text-xs text-muted-foreground">
              Last changed by {settings.updated_by_name} · {fmtWhen(settings.updated_at)}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Before you go live</CardTitle></CardHeader>
        <CardContent>
          <ul className="text-sm text-muted-foreground space-y-2">
            {[
              "Read every template in Email flows and send yourself a copy of each.",
              "Arm one flow at a time — Getting started can reach clients while payment escalation stays paused.",
              "Preview the escalation emails against a real overdue project — the copy reads differently with a five-figure balance in it.",
              "Check the Projects tab for trackers with no client email: those clients can't be reminded at all.",
              "Run test mode for a few days first — every email still gets rendered and logged, so you see exactly what would have gone out.",
              "Remember the reminders only fire when something calls the escalation — a scheduler, or Run now on a project.",
            ].map((line, i) => (
              <li key={i} className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────────── */

/* ── The lifecycle map ──────────────────────────────────────────────────────
 *
 * The whole point of this tab: HQ sends mail from 21 places across 6 modules,
 * and until now only the 9 tracker templates had a console. Everything else —
 * the kickoff email a client actually receives first, change orders, meetings,
 * the internal alerts — was invisible unless you read the code.
 *
 * Read as a vertical timeline, because the question people ask is sequential:
 * "what does a client get, and in what order?" Automatic sends are marked,
 * since those are the ones nobody chooses.
 */

const AUDIENCE = {
  client: { label: "Client", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  internal: { label: "Internal", cls: "bg-muted text-muted-foreground border-border" },
  staff: { label: "Staff", cls: "bg-muted text-muted-foreground border-border" },
};

function MapTab({ map, loading, onOpen }) {
  if (loading) return <p className="text-sm text-muted-foreground">Loading the map…</p>;
  if (!map) return <p className="text-sm text-muted-foreground">Couldn't load the map.</p>;

  const t = map.totals || {};
  const governed = new Set(map.tracker_mode_governs || []);

  return (
    <div className="space-y-5">
      {/* The four numbers that answer "how exposed are we?" */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { n: t.total, l: "Emails HQ can send" },
          { n: t.client_facing, l: "Reach a client" },
          { n: t.automatic, l: "Send automatically" },
          { n: t.automatic_client, l: "Automatic AND client-facing" },
        ].map((s) => (
          <Card key={s.l}>
            <CardContent className="pt-5 pb-4">
              <p className="text-2xl font-light tabular-nums">{s.n ?? "—"}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.l}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="text-xs text-muted-foreground max-w-3xl">
        The arm switches on <span className="font-medium">Settings</span> only govern the
        client-tracker emails ({governed.size} rows below, each carrying the mode its flow
        is in). Everything else — the welcome email included — sends regardless of them.
      </p>

      {/* Every flow's current state, so the map answers "will this one actually
          go out" without a trip to Settings. */}
      {(map.tracker_flow_modes || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {map.tracker_flow_modes.map((f) => (
            <span key={f.key} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              {f.name}
              <ModePill mode={f.mode} />
            </span>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {(map.stages || []).map((stage) => (
          <Card key={stage.key}>
            <CardHeader className="pb-3">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <CardTitle className="text-base">{stage.name}</CardTitle>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {stage.count} {stage.count === 1 ? "email" : "emails"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{stage.blurb}</p>
            </CardHeader>
            <CardContent className="pt-0">
              {stage.count === 0 ? (
                <p className="text-sm text-muted-foreground italic border-l-2 pl-3 py-1">
                  Nothing is emailed at this stage.
                </p>
              ) : (
                <div className="space-y-3">
                  {stage.emails.map((e) => (
                    <div key={e.key} className="border-l-2 pl-3 py-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-medium">{e.name}</span>
                        <Pill cls={AUDIENCE[e.audience]?.cls}>
                          {AUDIENCE[e.audience]?.label || e.audience}
                        </Pill>
                        {e.automatic
                          ? <Pill cls="bg-amber-100 text-amber-800 border-amber-200">Automatic</Pill>
                          : <Pill cls="bg-muted text-muted-foreground border-border">Manual</Pill>}
                        {governed.has(e.key) && (
                          e.send_mode
                            ? <ModePill mode={e.send_mode} />
                            : <Pill cls="bg-muted text-muted-foreground border-border">
                                armed by tracker
                              </Pill>
                        )}
                      </div>

                      <dl className="mt-1.5 text-xs text-muted-foreground space-y-0.5">
                        <div className="flex gap-2">
                          <dt className="w-16 shrink-0 font-medium">Goes to</dt>
                          <dd>{e.recipient}</dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="w-16 shrink-0 font-medium">Fires on</dt>
                          <dd>{e.trigger}</dd>
                        </div>
                        {e.subject && (
                          <div className="flex gap-2">
                            <dt className="w-16 shrink-0 font-medium">Subject</dt>
                            <dd className="font-mono text-[11px]">{e.subject}</dd>
                          </div>
                        )}
                      </dl>

                      {e.notes && (
                        <p className="text-xs mt-1.5 max-w-3xl">{e.notes}</p>
                      )}

                      {/* Editable copy links straight into the editor rather than
                          being described second-hand. */}
                      {(e.template || e.templates) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(e.templates || [e.template]).map((tpl) => (
                            <button
                              key={tpl.key}
                              type="button"
                              onClick={() => onOpen(tpl.key)}
                              className="text-[11px] px-2 py-0.5 rounded border hover:bg-muted"
                              data-testid={`map-template-${tpl.key}`}
                            >
                              Edit copy: {tpl.name || tpl.key}
                              {tpl.is_customised ? " ·  edited" : ""}
                            </button>
                          ))}
                        </div>
                      )}

                      <p className="text-[11px] text-muted-foreground/70 mt-1.5 font-mono">
                        {e.source?.file}:{e.source?.symbol}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}


export default function TrackerAdmin() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [flows, setFlows] = useState(null);
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openTemplate, setOpenTemplate] = useState(null);
  const [openLog, setOpenLog] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, f, m] = await Promise.all([
        apiClient.get("/tracker-admin/overview"),
        apiClient.get("/tracker-admin/flows"),
        apiClient.get("/tracker-admin/email-map"),
      ]);
      setOverview(o.data);
      setFlows(f.data);
      setMap(m.data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else toast.error(e.response?.data?.detail || "Couldn't load the console");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const settings = flows?.settings || overview?.settings;

  if (denied) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <AlertTriangle className="w-6 h-6 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-xl font-light">Owner access only</h1>
        <p className="text-sm text-muted-foreground mt-2">
          This console decides what every client is told and whether the emails are armed,
          so it's limited to owners and admins.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
      <div>
        <h1 className="text-xl font-light flex items-center gap-2">
          <Mail className="w-5 h-5" /> Tracker Admin
        </h1>
        <p className="text-sm text-muted-foreground">
          Everything the Client Tracker sends, everything it has sent, and where every project
          stands from both sides.
        </p>
      </div>

      <ModeBanner settings={settings} onGo={() => { setTab("settings"); setOpenTemplate(null); setOpenLog(null); }} />

      <div className="flex gap-1.5 flex-wrap border-b border-border pb-2">
        {TABS.map((t) => (
          <button key={t.key} title={t.hint}
            onClick={() => { setTab(t.key); setOpenTemplate(null); setOpenLog(null); }}
            className={`text-sm px-3 py-1.5 rounded-full transition-colors ${
              tab === t.key ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab data={overview} loading={loading} />}

      {tab === "flows" && (openTemplate ? (
        <TemplateEditor
          templateKey={openTemplate}
          projects={overview?.rows}
          onClose={() => setOpenTemplate(null)}
          onSaved={load}
        />
      ) : (
        <FlowsTab flows={flows} loading={loading} onOpen={setOpenTemplate} />
      ))}

      {tab === "map" && (openTemplate ? (
        <TemplateEditor
          templateKey={openTemplate}
          projects={overview?.rows}
          onClose={() => setOpenTemplate(null)}
          onSaved={load}
        />
      ) : (
        <MapTab map={map} loading={loading} onOpen={setOpenTemplate} />
      ))}

      {tab === "log" && (openLog
        ? <LogDetail logId={openLog} onClose={() => setOpenLog(null)} />
        : <LogTab onOpen={setOpenLog} />)}

      {tab === "settings" && <SettingsTab settings={settings} onSaved={load} />}
    </div>
  );
}
